import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Archive, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import stoplightAmber from "../assets/images/stoplight-amber.svg";
import stoplightGreen from "../assets/images/stoplight-green.svg";
import stoplightRed from "../assets/images/stoplight-red.svg";
import { toast } from "sonner";

import { BoardFilters } from "@/components/BoardFilters";
import { Header } from "@/components/Header";
import { MoveRunner } from "@/components/MoveRunner";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";
import { DraggableCard, TaskCard } from "@/components/board/TaskCard";
import {
  archiveTask,
  type BoardFilters as Filters,
  type BoardTask,
  filtersFromSearch,
  filtersToSearch,
  type SignoffStatus,
  unarchiveTask,
} from "@/lib/board";
import { rememberSpace, resolveDefaultSpace } from "@/lib/activeSpace";
import { moveAction, type PendingMove } from "@/lib/moves";
import { canDecide } from "@/lib/perms";
import {
  boardColumnOptions,
  qk,
  spacesQueryOptions,
  useUser,
} from "@/lib/queries";
import { SIGNOFF_COLOR } from "@/lib/stoplight";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";

// The board's three live columns (archived tasks live in the Archived view).
type ColumnStatus = Exclude<SignoffStatus, "archived">;

// Flow order: new tasks enter Pending review (left) and move rightward.
// Stoplight colors live on each column header (semantic), not the column order.
const COLUMNS: { status: ColumnStatus; label: string }[] = [
  { status: "pending_review", label: "Pending review" },
  { status: "changes_requested", label: "Changes requested" },
  { status: "accepted", label: "Accepted" },
];

// The column's stoplight icon, matching the logo's lights.
const STOPLIGHT_SVG: Record<ColumnStatus, string> = {
  pending_review: stoplightAmber,
  changes_requested: stoplightRed,
  accepted: stoplightGreen,
};

// A move in flight: while dragging (`drag`) or after the drop, while the
// mutation settles (`pending`).
// Drives the source-collapse + destination-insertion preview across columns.
type BoardMove = {
  task: BoardTask;
  from: SignoffStatus;
  to: SignoffStatus;
  kind: "drag" | "pending";
};

function BoardColumn({
  status,
  spaceId,
  label,
  filters,
  move,
  placeholderHeight,
  canDecide,
  onOpen,
  onArchive,
}: {
  status: ColumnStatus;
  spaceId: string;
  label: string;
  filters: Filters;
  move: BoardMove | null;
  placeholderHeight: number | undefined;
  canDecide: boolean;
  onOpen: (id: string) => void;
  onArchive: (id: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { setNodeRef: dropRef } = useDroppable({ id: status });

  // useInfiniteQuery gives native stale-while-revalidate: cached cards paint
  // instantly on remount while a background refetch revalidates them.
  const {
    data,
    isPending,
    isError,
    fetchStatus,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(boardColumnOptions(status, spaceId, filters));
  // `enabled:false` (no space resolved yet) keeps the query "pending" but idle —
  // don't show the spinner for that; let it fall through to the empty state.
  const loading = isPending && fetchStatus !== "idle";

  let tasks = data?.pages.flatMap((p) => p.tasks) ?? [];
  let total = data?.pages[0]?.total ?? 0;
  const hasMore = hasNextPage;

  // Live preview: the destination opens an insertion slot at the top — a dashed
  // gap while dragging, the real card once dropped (until the move settles).
  const isSource = move?.from === status;
  const isDest = move?.to === status;
  const pending = move?.kind === "pending";

  // While a dropped move is settling its dimmed preview stands in for the card,
  // so the real card must not render alongside it. The destination is the subtle
  // case: `settle()` awaits the board refetch BEFORE clearing the pending move,
  // so the refetched card can land in this column's query data while the preview
  // is still shown — rendering it twice. Filtering the moved card out of
  // whichever column currently holds it keeps exactly one copy on screen.
  // (During a live drag we leave the source card in place — unmounting the active
  // draggable mid-drag breaks dnd-kit's drop detection.)
  const hasMovedCard =
    !!move && (isSource || isDest) && tasks.some((t) => t.id === move.task.id);
  if (pending && hasMovedCard && move) {
    tasks = tasks.filter((t) => t.id !== move.task.id);
  }

  // Keep the count in step with the preview, without double-adjusting once a
  // refetch has already shifted the card between the columns' server totals.
  if (isSource) total = Math.max(0, total - (pending && !hasMovedCard ? 0 : 1));
  else if (isDest) total = total + (pending && hasMovedCard ? 0 : 1);

  // Lazy-load the next page when the sentinel nears the column's scroll bottom.
  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rootRef: scrollRef,
  });

  return (
    <section ref={dropRef} className="flex min-h-0 flex-1 flex-col">
      <div className="mb-3 flex shrink-0 items-center gap-2.5">
        <img
          src={STOPLIGHT_SVG[status]}
          alt=""
          aria-hidden="true"
          className="size-5 shrink-0"
        />
        <h2 className="text-sm font-semibold text-foreground">{label}</h2>
        <span
          className="ml-auto rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{
            backgroundColor: `${SIGNOFF_COLOR[status]}26`,
            color: SIGNOFF_COLOR[status],
          }}
        >
          {total}
        </span>
      </div>
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-4 pr-1"
      >
        {loading ? (
          <p className="px-3 py-6 text-center text-xs text-muted-foreground">
            Loading…
          </p>
        ) : isError ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-6 text-center text-xs text-destructive">
            Couldn't load
          </p>
        ) : tasks.length === 0 && !isDest ? (
          <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            Nothing here
          </p>
        ) : (
          <>
            {isDest &&
              (move.kind === "pending" ? (
                // Dropped, mutation in flight — the real card (dimmed), not
                // draggable. No ring/frame; the dim alone marks it as tentative.
                <TaskCard
                  task={move.task}
                  className="cursor-default opacity-60"
                />
              ) : (
                // While dragging — a dashed gap the size of the card marks where
                // it'll land; the cards below shift down to make room.
                <div
                  aria-hidden="true"
                  className="shrink-0 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5"
                  style={{ height: placeholderHeight ?? 96 }}
                />
              ))}
            {tasks.map((task) => (
              <DraggableCard
                key={task.id}
                task={task}
                // A pending move locks the board until it settles. Since accept
                // and reopen no longer open a modal, this is the only thing
                // serializing drops — without it a second drop would clear the
                // first one's preview and drag from a stale source column.
                disabled={!canDecide || move?.kind === "pending"}
                onOpen={() => onOpen(task.id)}
                onArchive={canDecide ? onArchive : undefined}
              />
            ))}
            {hasMore && <div ref={sentinelRef} className="h-1 shrink-0" />}
          </>
        )}
      </div>
    </section>
  );
}

const route = getRouteApi("/");

export function Dashboard() {
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The open task's modal is URL-driven: ?task=<id> (deep-linkable, e.g. from
  // a notification). Board filters live in the same search params so a filtered
  // view is shareable and survives reload.
  const search = route.useSearch();
  const openTaskId = search.task;
  // Memoized so its identity is stable across renders (it's a query-key input
  // and a prop to BoardColumn/BoardFilters).
  const filters = useMemo(() => filtersFromSearch(search), [search]);
  const decider = canDecide(user);

  // Active space is URL-driven (?space=<id>), per viewer. Seed it from the
  // remembered choice (or the first space) whenever the URL doesn't name one, so
  // the board always shows a single space and the choice survives reloads.
  const { data: spaces = [] } = useQuery(spacesQueryOptions);
  const spaceId = search.space_id ?? "";
  useEffect(() => {
    if (search.space_id) {
      rememberSpace(search.space_id);
      return;
    }
    const target = resolveDefaultSpace(spaces);
    if (target)
      void navigate({
        to: "/",
        search: (prev) => ({ ...prev, space_id: target }),
        replace: true,
      });
  }, [search.space_id, spaces, navigate]);

  // A dropped move that's still being applied (accept and reopen fire straight
  // away; only request changes waits, on its feedback form), the card being
  // dragged + its size (for the overlay clone and the gap), and the column
  // currently hovered (for the live insertion preview).
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [activeTask, setActiveTask] = useState<BoardTask | null>(null);
  const [activeSize, setActiveSize] = useState<{ w: number; h: number }>();
  const [overStatus, setOverStatus] = useState<SignoffStatus | null>(null);

  // distance:8 lets a plain click open the modal; only a real drag starts a move.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Stable identity so the quick-search debounce effect in BoardFilters doesn't
  // reset on every render. Preserves the open task; replaces only filter params.
  const handleFilterChange = useCallback(
    (next: Filters) =>
      void navigate({
        to: "/",
        search: (prev) => ({ ...prev, ...filtersToSearch(next) }),
      }),
    [navigate],
  );

  const openTask = (id: string) =>
    void navigate({ to: "/", search: (prev) => ({ ...prev, task: id }) });

  // Manual per-card archive (the ⋯ menu). Archiving drops the card off the live
  // board; the toast offers Undo (unarchive → back to Pending review, exempt).
  const invalidateArchive = () => {
    void queryClient.invalidateQueries({ queryKey: qk.board });
    void queryClient.invalidateQueries({ queryKey: qk.archived });
  };
  const unarchiveMut = useMutation({
    mutationFn: (id: string) => unarchiveTask(id),
    onSuccess: () => {
      toast.success("Task restored to review", {
        icon: <RotateCcw className="size-4" />,
      });
      invalidateArchive();
    },
    onError: () => toast.error("Couldn't restore the task"),
  });
  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveTask(id),
    onSuccess: (_r, id) => {
      toast.success("Task archived", {
        icon: <Archive className="size-4" />,
        action: { label: "Undo", onClick: () => unarchiveMut.mutate(id) },
      });
      invalidateArchive();
    },
    onError: () => toast.error("Couldn't archive the task"),
  });

  function handleDragStart(event: DragStartEvent) {
    setActiveTask((event.active.data.current?.task as BoardTask) ?? null);
    const r = event.active.rect.current.initial;
    setActiveSize(r ? { w: r.width, h: r.height } : undefined);
  }

  function handleDragOver(event: DragOverEvent) {
    setOverStatus((event.over?.id as SignoffStatus | undefined) ?? null);
  }

  function resetDrag() {
    setActiveTask(null);
    setOverStatus(null);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    resetDrag();
    if (!over) return;
    const from = active.data.current?.from as SignoffStatus | undefined;
    const task = active.data.current?.task as BoardTask | undefined;
    if (!from || !task) return;
    const action = moveAction(from, over.id as SignoffStatus);
    if (!action) return; // same column or unsupported transition → snaps back
    setPendingMove({ task, from, to: over.id as SignoffStatus, action });
  }

  // Unified preview the columns consume: a dropped move outranks the live
  // drag-hover preview (only valid transitions produce one).
  const move: BoardMove | null = pendingMove
    ? {
        task: pendingMove.task,
        from: pendingMove.from,
        to: pendingMove.to,
        kind: "pending",
      }
    : activeTask &&
        overStatus &&
        moveAction(activeTask.signoffStatus, overStatus)
      ? {
          task: activeTask,
          from: activeTask.signoffStatus,
          to: overStatus,
          kind: "drag",
        }
      : null;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      <Header />

      <main className="mx-auto flex w-full min-h-0 max-w-6xl flex-1 flex-col overflow-hidden px-6 pt-6">
        <div className="mb-6 shrink-0">
          <BoardFilters
            filters={filters}
            onChange={handleFilterChange}
            spaceId={spaceId}
          />
        </div>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={resetDrag}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-6 md:flex-row">
            {COLUMNS.map(({ status, label }) => (
              <BoardColumn
                key={status}
                status={status}
                spaceId={spaceId}
                label={label}
                filters={filters}
                move={move}
                placeholderHeight={activeSize?.h}
                canDecide={decider}
                onOpen={openTask}
                onArchive={(id) => archiveMut.mutate(id)}
              />
            ))}
          </div>
          <DragOverlay>
            {activeTask ? (
              <TaskCard
                task={activeTask}
                style={{ width: activeSize?.w }}
                className="rotate-2 cursor-grabbing shadow-xl"
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </main>

      <MoveRunner
        move={pendingMove}
        onCancel={() => setPendingMove(null)}
        // Clear only the move that settled: an Undo queues its reopen while the
        // accept it undoes is still finishing, and that reopen has to survive.
        onConfirmed={(id, action) =>
          setPendingMove((cur) =>
            cur?.task.id === id && cur.action === action ? null : cur,
          )
        }
        onUndo={(task) =>
          setPendingMove({
            // The task was accepted server-side, so the dimmed preview has to
            // render it that way — `task` still carries the pending-review
            // status it was dragged with.
            task: { ...task, signoffStatus: "accepted" },
            from: "accepted",
            to: "pending_review",
            action: "reopen",
          })
        }
      />

      <TaskDetailDialog
        taskId={openTaskId ?? null}
        canDecide={decider}
        onOpenChange={(open) => {
          // Closing drops the ?task= param (URL drives the modal) but keeps the
          // active filters in the URL.
          if (!open)
            void navigate({
              to: "/",
              search: (prev) => ({ ...prev, task: undefined }),
            });
        }}
      />
    </div>
  );
}

export default Dashboard;
